import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '@/theme/ThemeProvider';
import {
  useCookieConsentStore,
  isConsentExpired,
} from '@/app/hooks/cookieConsent';
import {
  CookieBannerContainer,
  CookieBannerContent,
  CookieTextSection,
  CookieTitle,
  CookieMessage,
  CookieActionsSection,
  CookieButtonGroup,
  CookieButton,
  CookieManageLink,
} from './styles';

const CookieBanner: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = React.useContext(ThemeContext);
  const [shouldShow, setShouldShow] = useState(false);

  const isApp =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('app') === 'true';

  const { hasConsented, consentDate, acceptAll, rejectOptional, reset } =
    useCookieConsentStore();

  useEffect(() => {
    // 동의하지 않았거나 동의가 만료된 경우 배너 표시
    if (!hasConsented || isConsentExpired(consentDate)) {
      // 만료된 경우 기존 동의 초기화
      if (isConsentExpired(consentDate)) {
        reset();
      }
      setShouldShow(true);
    } else {
      setShouldShow(false);
    }
  }, [hasConsented, consentDate, reset]);

  const handleAcceptAll = () => {
    acceptAll();
    setShouldShow(false);
  };

  const handleRejectOptional = () => {
    rejectOptional();
    setShouldShow(false);
  };

  const handleManageCookies = () => {
    // TODO: 쿠키 관리 모달 열기 또는 개인정보처리방침 페이지로 이동
    // 현재는 개인정보처리방침 페이지로 이동하도록 구현
    window.open('/privacy-policy', '_blank');
  };

  if (!shouldShow || isApp) return null;

  const banner = (
    <CookieBannerContainer theme={theme}>
      <CookieBannerContent>
        <CookieTextSection>
          <CookieTitle>{t('cookieConsent.title')}</CookieTitle>
          <CookieMessage>{t('cookieConsent.message')}</CookieMessage>
        </CookieTextSection>

        <CookieActionsSection>
          <CookieButtonGroup>
            <CookieButton $variant="primary" onClick={handleAcceptAll}>
              {t('cookieConsent.acceptAll')}
            </CookieButton>
            <CookieButton $variant="secondary" onClick={handleRejectOptional}>
              {t('cookieConsent.rejectOptional')}
            </CookieButton>
          </CookieButtonGroup>
          <CookieManageLink onClick={handleManageCookies}>
            {t('cookieConsent.manageCookies')} →
          </CookieManageLink>
        </CookieActionsSection>
      </CookieBannerContent>
    </CookieBannerContainer>
  );

  // Portal을 사용하여 body 최상위에 렌더링
  return createPortal(banner, document.body);
};

export default CookieBanner;
